import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Typography, 
  Card, 
  Button, 
  Space, 
  Avatar, 
  Tag, 
  Divider, 
  Row, 
  Col, 
  Tooltip,
  Modal,
  message,
  Spin,
  Breadcrumb,
  Statistic,
  Input,
  Form
} from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  UserOutlined, 
  ArrowLeftOutlined,
  WalletOutlined,
  HistoryOutlined,
  CopyOutlined,
  ExportOutlined,
  InfoCircleOutlined,
  GlobalOutlined,
  ArrowUpOutlined,
  BankOutlined,
  SafetyOutlined,
  TransactionOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAdmin } from '../../hooks/useAdmin';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;

const WithdrawalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getWithdrawals, getUserWallet, processWithdrawal, loading: actionLoading } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [withdrawal, setWithdrawal] = useState(null);
  const [userBalance, setUserBalance] = useState('0.00');
  const [form] = Form.useForm();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch withdrawal details (filtering by ID from the list as there's no direct getById)
      const wRes = await getWithdrawals({ status: 'requested' });
      if (wRes.success) {
        const found = wRes.data.find(w => String(w.id) === String(id));
        if (found) {
          setWithdrawal(found);
          
          // 2. Fetch User Wallet to get the specific balance for the requested asset
          const walletRes = await getUserWallet(found.user_id);
          if (walletRes.success && walletRes.data.wallets) {
            // Filter spot wallet for the specific asset
            const spotWallet = walletRes.data.wallets.find(w => 
              w.wallet_type === 'spot' && 
              (w.symbol === found.symbol || w.asset === found.asset)
            );
            setUserBalance(spotWallet ? spotWallet.balance : '0.00');
          }
        } else {
          message.error('Withdrawal request not found');
          navigate('/manager/withdrawals');
        }
      }
    } catch (err) {
      message.error('Error loading withdrawal details');
    } finally {
      setLoading(false);
    }
  }, [id, getWithdrawals, getUserWallet, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAction = async (status) => {
    const actionText = status === 'admin_approved' ? t('manager.withdrawals.actions.approve') : t('manager.withdrawals.actions.reject');
    
    Modal.confirm({
      title: `${actionText} Withdrawal?`,
      className: 'dark-modal',
      content: (
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item name="admin_note" label={t('manager.withdrawals.form.note')}>
            <Input.TextArea 
              rows={3} 
              placeholder={t('manager.withdrawals.form.note_placeholder')} 
              className="bg-slate-900 border-white/10 text-white"
            />
          </Form.Item>
        </Form>
      ),
      okText: t('manager.withdrawals.actions.confirm'),
      cancelText: t('manager.withdrawals.actions.cancel'),
      onOk: async () => {
        const values = form.getFieldsValue();
        try {
          const res = await processWithdrawal(id, { 
            action: status === 'admin_approved' ? 'approve' : 'reject', 
            notes: values.admin_note 
          });
          if (res.success) {
            message.success(t('manager.withdrawals.messages.process_success'));
            navigate('/manager/withdrawals');
          } else {
            message.error(res.message || t('common.error'));
          }
        } catch (err) {
          message.error(t('common.error'));
        }
      }
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success('Address copied to clipboard');
  };

  if (loading || !withdrawal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Space direction="vertical" align="center">
          <Spin size="large" />
          <Text className="text-slate-400 animate-pulse uppercase tracking-[0.2em] text-xs">{t('common.loading')}</Text>
        </Space>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 min-h-screen bg-slate-950 text-slate-200 animate-fade-in">
      {/* Navigation & Breadcrumbs */}
      <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <Space direction="vertical" size={4}>
          <Breadcrumb 
            className="mb-2"
            items={[
              { title: <Link to="/manager" className="text-slate-500 hover:text-white transition-colors">{t('manager.dashboard.title')}</Link> },
              { title: <Link to="/manager/withdrawals" className="text-slate-500 hover:text-white transition-colors">{t('manager.withdrawals.title')}</Link> },
              { title: <Text className="text-orange-400 font-bold uppercase tracking-widest text-[10px]">Detail</Text> }
            ]}
          />
          <Space align="center">
            <Button 
              icon={<ArrowLeftOutlined />} 
              type="link" 
              onClick={() => navigate('/manager/withdrawals')}
              className="text-slate-400 hover:text-white p-0 mr-2"
            />
            <Title level={2} className="m-0 !text-white !font-black uppercase tracking-tight">
              Withdrawal <span className="text-orange-500">#{id?.slice(0, 8)}</span>
            </Title>
          </Space>
        </Space>

        <Space>
          <Button 
            icon={<CloseCircleOutlined />} 
            onClick={() => handleAction('rejected')}
            className="btn-pro bg-rose-500/10 text-rose-500 border-rose-500/20 hover:bg-rose-500/20"
          >
            {t('manager.withdrawals.actions.reject')}
          </Button>
          <Button 
            type="primary"
            icon={<CheckCircleOutlined />} 
            onClick={() => handleAction('admin_approved')}
            className="btn-pro bg-emerald-500 border-none text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/20"
          >
            {t('manager.withdrawals.actions.approve')}
          </Button>
        </Space>
      </div>

      <Row gutter={[24, 24]}>
        {/* Withdrawal Summary Card */}
        <Col xs={24} lg={14}>
          <Card className="trading-card border-none overflow-hidden relative mb-6 bg-gradient-to-br from-slate-900 to-slate-950">
            <div className="absolute top-0 right-0 p-8 opacity-5 text-8xl">
              <TransactionOutlined />
            </div>
            
            <Row gutter={24} align="middle">
              <Col xs={24} md={12}>
                <div className="mb-8 md:mb-0">
                  <Text className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] block mb-2">{t('manager.withdrawals.table.amount')}</Text>
                  <div className="flex items-baseline gap-2">
                    <Title level={1} className="!text-white !m-0 !font-black !text-4xl">{withdrawal?.amount}</Title>
                    <Text className="text-orange-500 font-black text-xl">{withdrawal?.symbol || withdrawal?.currency}</Text>
                  </div>
                  <Space className="mt-2">
                    <Tag color="orange" className="font-black uppercase tracking-widest text-[9px] border-none bg-orange-500/10 text-orange-400">
                      {withdrawal?.status?.toUpperCase()}
                    </Tag>
                    <Text className="text-slate-500 text-[10px] font-bold">Net: {withdrawal?.net_amount || withdrawal?.amount} ({t('manager.withdrawals.table.fee')}: {withdrawal?.fee || '0.00'})</Text>
                  </Space>
                </div>
              </Col>
              
              <Col xs={24} md={12}>
                <Space direction="vertical" className="w-full" size={12}>
                  <div className="flex justify-between items-center py-2 border-b border-white/5">
                    <Space className="text-slate-500">
                      <HistoryOutlined className="text-xs" />
                      <Text className="text-[10px] font-bold uppercase tracking-wider">{t('manager.withdrawals.table.date')}</Text>
                    </Space>
                    <Text className="text-white text-xs font-bold">{moment(withdrawal?.created_at).format('YYYY-MM-DD HH:mm')}</Text>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-white/5">
                    <Space className="text-slate-500">
                      <GlobalOutlined className="text-xs" />
                      <Text className="text-[10px] font-bold uppercase tracking-wider">{t('manager.withdrawals.table.network')}</Text>
                    </Space>
                    <Tag className="m-0 border-white/10 bg-white/5 text-white text-[10px] font-black">{withdrawal?.network || 'CRYPTO'}</Tag>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <Space className="text-slate-500">
                      <BankOutlined className="text-xs" />
                      <Text className="text-[10px] font-bold uppercase tracking-wider">Status</Text>
                    </Space>
                    <Text className="text-amber-500 text-xs font-black uppercase">Awaiting Approval</Text>
                  </div>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Destination Details */}
          <Card 
            className="trading-card border-none"
            title={<Text className="text-white font-bold uppercase tracking-widest text-xs flex items-center gap-2"><ArrowUpOutlined className="text-orange-500" /> {t('manager.withdrawals.table.destination')}</Text>}
          >
            <div className="p-6 bg-slate-950 rounded-2xl border border-white/5 relative group">
              <div className="flex justify-between items-start mb-4">
                <Text className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Withdrawal Address</Text>
                <Space>
                  <Tooltip title="Copy">
                    <Button 
                      type="text" 
                      icon={<CopyOutlined />} 
                      onClick={() => copyToClipboard(withdrawal?.address)}
                      className="text-slate-500 hover:text-white"
                    />
                  </Tooltip>
                  <Tooltip title="View on Explorer">
                    <Button 
                      type="text" 
                      icon={<ExportOutlined />} 
                      className="text-slate-500 hover:text-white"
                    />
                  </Tooltip>
                </Space>
              </div>
              <Paragraph className="!text-orange-400 font-mono text-lg break-all !m-0 font-bold tracking-tight">
                {withdrawal?.address}
              </Paragraph>
              
              <div className="mt-6 flex items-start gap-3 p-3 bg-amber-500/5 border border-amber-500/10 rounded-xl">
                <InfoCircleOutlined className="text-amber-500 mt-0.5" />
                <Text className="text-amber-500/80 text-[10px] font-medium leading-relaxed uppercase tracking-wider">
                  Important: Always verify the destination network ({withdrawal?.network}) matches the address format before approving. Funds sent to wrong networks are non-recoverable.
                </Text>
              </div>
            </div>
          </Card>
        </Col>

        {/* User Sidebar Info */}
        <Col xs={24} lg={10}>
          <Card className="trading-card border-none h-full relative">
            <Title level={4} className="!text-white font-black uppercase tracking-widest !text-xs mb-6 flex items-center gap-2">
              <UserOutlined className="text-indigo-500" /> User Profile
            </Title>
            
            <div className="p-6 bg-white/5 rounded-2xl border border-white/5 mb-6">
              <div className="flex items-center gap-4 mb-6">
                <Avatar size={64} className="bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 flex items-center justify-center">
                  {withdrawal?.username ? withdrawal?.username[0] : 'U'}
                </Avatar>
                <div>
                  <Title level={4} className="!text-white !m-0 !font-black uppercase tracking-tight">{withdrawal?.username}</Title>
                  <Text className="text-slate-500 text-xs font-medium">User ID: {withdrawal?.user_id}</Text>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-950 rounded-xl border border-white/5">
                  <Text className="text-[9px] font-black text-slate-600 uppercase tracking-widest block mb-1">{t('manager.withdrawals.table.spot_balance')}</Text>
                  <Text className="text-white font-black text-sm">{userBalance} <span className="text-indigo-500 text-[10px]">{withdrawal?.symbol || withdrawal?.currency}</span></Text>
                </div>
                <div className="p-3 bg-slate-950 rounded-xl border border-white/5">
                  <Text className="text-[9px] font-black text-slate-600 uppercase tracking-widest block mb-1">{t('manager.withdrawals.table.status')}</Text>
                  <Tag color="success" className="m-0 border-none bg-emerald-500/10 text-emerald-500 font-black text-[9px] uppercase tracking-widest px-2 py-0">ACTIVE</Tag>
                </div>
              </div>
            </div>

            <Space direction="vertical" className="w-full" size={12}>
              <div className="flex items-center gap-3 p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-xl">
                <SafetyOutlined className="text-emerald-500 text-lg" />
                <div>
                  <Text className="text-white text-xs font-bold block">Account Status: Secured</Text>
                  <Text className="text-slate-500 text-[10px] block">This user has passed all security checks and 2FA is active.</Text>
                </div>
              </div>
              
              <Divider className="border-white/5 m-2" />
              
              <Button 
                block 
                className="btn-pro bg-white/5 border-white/10 text-slate-400 hover:text-white"
                onClick={() => navigate(`/admin/users/${withdrawal?.user_id}`)}
              >
                View Full User Portfolio
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      <style jsx="true">{`
        .ant-breadcrumb li:last-child {
          color: #f97316 !important;
        }
        .ant-breadcrumb .ant-breadcrumb-separator {
          color: rgba(255, 255, 255, 0.1) !important;
        }
        .dark-modal .ant-modal-content {
          background: #0f172a !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
        }
        .dark-modal .ant-modal-header {
          background: transparent !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
        }
        .dark-modal .ant-modal-title {
          color: white !important;
        }
        .dark-modal .ant-modal-footer {
          border-top: 1px solid rgba(255, 255, 255, 0.05) !important;
        }
      `}</style>
    </div>
  );
};

export default WithdrawalDetail;

